/** @jsx jsx */
import {
  Box,
  Button,
  Container,
  Flex,
  FullScreenModal,
  FullScreenModalContainer,
  FullScreenModalHeader,
  FullScreenModalHeading,
  FullScreenModalProps,
  TabButton,
} from "@eworkit/iris";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { jsx } from "theme-ui";

import { FullScreenModalFloatingActions } from "@/components/dialogs";
import { ResponsiveCard } from "@/components/ResponsiveCard";
import { TimeReport, useTimeReportAggregate } from "@/data/time-reports";

import { TimeReportCalendar, TimeReportCalendarEmptyState } from "../calendar";
import { TimeReportHeader } from "../header";
import { TimeReportSpecification } from "../specification/TimeReportSpecification";
import { TimeReportSpecificationEmptyState } from "../specification/TimeReportSpecificationEmptyState";
import { TimeReportEditProvider } from "./TimeReportEditContext";

interface EditTimeReportModalProps extends FullScreenModalProps {
  timeReport: TimeReport;
  refetchTimeReport: () => Promise<unknown>;
  onSubmitClick: () => void;
}

export const EditTimeReportModal: FC<EditTimeReportModalProps> = ({
  timeReport,
  refetchTimeReport,
  onSubmitClick,
  ...modalProps
}) => {
  const { t } = useTranslation("session");

  const [tab, setTab] = useState<"calendar" | "specification">("calendar");

  const specificationAggregateData = useTimeReportAggregate(timeReport, {
    from: new Date(timeReport.startDate),
    to: new Date(timeReport.endDate),
    filterEmptyItems: true,
  });

  return (
    <TimeReportEditProvider timeReport={timeReport} refetchTimeReport={refetchTimeReport}>
      <FullScreenModal {...modalProps} aria-label={t("TIME_REPORTS.EDIT.EDIT_TIME_REPORT")}>
        <FullScreenModalContainer>
          <FullScreenModalHeader
            onCloseClick={modalProps.dialogState.hide}
            closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}>
            <FullScreenModalHeading>{t("TIME_REPORTS.EDIT.EDIT_TIME_REPORT")}</FullScreenModalHeading>
          </FullScreenModalHeader>

          <Box py="md">
            <Container>
              <Flex flexDirection="column" gap="sm">
                <Box px="sm">
                  <TimeReportHeader timeReport={timeReport} />
                </Box>

                <Box px={[null, null, null, "sm"]}>
                  <ResponsiveCard
                    sx={{
                      borderColor: "vg500",
                      boxShadow: "0px 2px 4px rgba(12, 12, 13, 0.12)",
                      px: ["sm", "sm", "sm", "md"],
                      py: ["sm", "sm", "sm", "md"],
                    }}>
                    <Flex flexDirection="column" gap={["sm", null, null, "md"]}>
                      <Flex borderBottom="primary">
                        <TabButton active={tab === "calendar"} onClick={() => setTab("calendar")}>
                          {t("TIME_REPORTS.TABS.CALENDAR")}
                        </TabButton>
                        <TabButton active={tab === "specification"} onClick={() => setTab("specification")}>
                          {t("TIME_REPORTS.TABS.SPECIFICATION")}
                        </TabButton>
                      </Flex>

                      {tab === "calendar" &&
                        (!timeReport.projects.length ? (
                          <TimeReportCalendarEmptyState timeReport={timeReport} editable />
                        ) : (
                          <TimeReportCalendar timeReport={timeReport} editable />
                        ))}

                      {tab === "specification" &&
                        (specificationAggregateData.aggregateTotalReportedTime.isEmpty ? (
                          <TimeReportSpecificationEmptyState />
                        ) : (
                          <TimeReportSpecification
                            timeReport={timeReport}
                            aggregateData={specificationAggregateData}
                            editable
                          />
                        ))}
                    </Flex>
                  </ResponsiveCard>
                </Box>
              </Flex>
            </Container>

            <FullScreenModalFloatingActions>
              <Button variant="ghost" onClick={modalProps.dialogState.hide}>
                {t("TIME_REPORTS.EDIT.CLOSE")}
              </Button>
              <Button onClick={onSubmitClick}>{t("TIME_REPORTS.EDIT.SUBMIT_TIME_REPORT")}</Button>
            </FullScreenModalFloatingActions>
          </Box>
        </FullScreenModalContainer>
      </FullScreenModal>
    </TimeReportEditProvider>
  );
};
